/**
 * Payment / Loyalty / Gift Card / Wallet / Coupon services
 */
import axios from "axios";
import useAuthStore from "../stores/useAuthStore";
import useCartStore from "../stores/useCartStore";
import useTransactionStore from "../stores/useTransactionStore";
import { applyAuthInterceptors } from "./authInterceptor";
import { getOAuthHeaders } from "../constants/oAuthValidation";
import { applyHealthInterceptors } from "../utils/healthReporter";
import { API_TIMEOUTS } from "./api/client";

const retailURL = process.env.REACT_APP_serverUrl;
const retailInstance = axios.create();
retailInstance.defaults.baseURL = retailURL;
retailInstance.defaults.method = "POST";
retailInstance.defaults.headers.post["Content-Type"] = "application/json";
// Payment gateways can legitimately take long; use the payment tier so a
// dropped connection still fails within a bounded window instead of hanging.
retailInstance.defaults.timeout = API_TIMEOUTS.payment;

// Dynamic tenant baseURL interceptor
retailInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_serverUrl;
  if (url) config.baseURL = url;
  return config;
});

// Auth interceptors: token attachment + 401 refresh/retry
applyAuthInterceptors(retailInstance);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(retailInstance);

const upsertLoyaltyTransaction = async (tillData, value, cwrCustomerId, referenceId) => {
  try {
    const queryPayload = {
      query: `mutation {
            upsertLoyaltyTransaction(transaction: [{
                csBunitId: "${tillData.tillAccess.csBunit.csBunitId}"
                loyaltyPoints: ${value}
                b2cCustomerId: "${cwrCustomerId}"
                referenceId: "${referenceId}"
              }]) {
              status
              message
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const verifyCredit = async (cwrCustomerId, amount) => {
  try {
    const queryPayload = {
      query: `query{
            verifyCredit(customerId:"${cwrCustomerId}", amount:${amount}){
                iscredit
                creditLimit
                creditBalance
                creditApproachPercentage
                code
                status
                message
            }
        }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const upsertCreditTransaction = async (transactionData) => {
  const stringifiedBody = JSON.stringify(transactionData).replace(/"/g, '\\"');
  try {
    const queryPayload = {
      query: `mutation {
        upsertCreditTransaction(creditTransactions: "${stringifiedBody}") {
          status
          message
        }
      }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const verifyLoyaltyRedemption = async (cwrCustomerId, csBunitId, loyalityPoints, cartTotal) => {
  const body = {
    customerId: cwrCustomerId,
    bunitId: csBunitId,
    points: loyalityPoints ? loyalityPoints : null,
    totalAmount: cartTotal,
  };
  const stringifiedBody = JSON.stringify(body).replace(/"/g, '\\"');
  try {
    const queryPayload = {
      query: `mutation {
        verifyLoyaltyRedemption(messageData: "${stringifiedBody}") {
          status
          message
        }
      }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const validOtp = async (cwrCustomerId, otp, loyalityPoints, type = "") => {
  const body = {
    customerId: cwrCustomerId,
    otp: otp,
    type: type,
    points: loyalityPoints ? loyalityPoints : null,
  };
  const stringifiedBody = JSON.stringify(body).replace(/"/g, '\\"');
  try {
    const queryPayload = {
      query: `mutation {
        validOtp(messageData: "${stringifiedBody}") {
          status
          message
        }
      }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const verifyWalletRedemption = async (customerId, bunitId, totalAmount) => {
  const body = { customerId, bunitId, totalAmount };
  const stringifiedBody = JSON.stringify(body).replace(/"/g, '\\"');

  const queryPayload = {
    query: `mutation {
      verifyWalletRedemption(messageData: "${stringifiedBody}") {
        status
        message
      }
    }`,
  };

  return retailInstance({ data: queryPayload });
};

const validOtpCoupon = async (userId, otp, couponData, mPricingRuleId) => {
  const cartObj = useCartStore.getState().cart;
  const mobileNo = cartObj?.customer?.mobileNo ? cartObj.customer.mobileNo : null;
  const body = {
    userId: userId,
    otp: otp,
    couponCode: couponData,
    contactNo: mobileNo,
    pricingRuleId: mPricingRuleId,
  };
  const stringifiedBody = JSON.stringify(body).replace(/"/g, '\\"');
  try {
    const queryPayload = {
      query: `mutation {
        validOtp(messageData: "${stringifiedBody}") {
          status
          message
        }
      }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const sendOtpToUser = async (userId, pricingRule, couponCode, discount, totalStd) => {
  const tillData = useAuthStore.getState().tillData;
  let couponInfo = {};
  const rawCouponInfo = useTransactionStore.getState().couponAdditionalInfo;
  if (rawCouponInfo) {
    couponInfo = rawCouponInfo;
  }
  const body = {
    couponCode: couponCode,
    discount: discount,
    totalStd: totalStd,
    discountInfo: couponInfo["Discount Reason"] || "",
    remarks: couponInfo["Remarks"] || "",
  };
  const stringifiedData = JSON.stringify(body).replace(/"/g, '\\"');
  try {
    const queryPayload = {
      query: `mutation {
  sendOtpToUser(userId: "${userId}", bunitId: "${tillData.tillAccess.csBunit.csBunitId}",pricingRuleId: "${pricingRule.mPricingrulesId}",  msgData: "${stringifiedData}" ) {
    status
    message
  }
}`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const discardGiftCard = async (cardNo, refNo, array = null) => {
  try {
    const giftCards = array
      ? JSON.stringify(array).replace(/"([^"]+)":/g, "$1:")
      : `[{ cardNo: "${cardNo}", referenceNo: "${refNo}" }]`;

    const queryPayload = {
      query: `mutation{
            discardGiftCard(giftCards: ${giftCards}) {
                status
                message
            }
        }`,
    };

    const response = await retailInstance({ data: queryPayload });
    return response;
  } catch (error) {
  }
};

const discardCouponService = async (couponInput, referenceId) => {
  try {
    const queryPayload = {
      query: `query{
            discardCoupon(couponcode:"${couponInput}",referenceId: "${referenceId}"){
                status
                message
            }
        }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const verifyGiftCardService = async (giftCardInput) => {
  try {
    const response = await retailInstance({
      data: giftCardInput,
    });
    return response;
  } catch (error) {
  }
};

const checkGiftCard = async (giftCardInput) => {
  try {
    const response = await retailInstance({
      data: giftCardInput,
    });
    return response;
  } catch (error) {
  }
};

const upsertGiftCardTransaction = async (giftCardTransactionInput) => {
  try {
    const response = await retailInstance({
      data: giftCardTransactionInput,
    });
    return response;
  } catch (error) {
    console.error("Error in upsertGiftCardTransaction:", error);
  }
};

const generateGiftVoucher = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in generate gift voucher service");
  }
};

const getEInvoiceDetails = async (queryPayload) => {
  try {
    const response = await retailInstance({ data: queryPayload });
    return response;
  } catch (error) {
    console.error(error, "getEInvoiceDetails error");
  }
};

const getEInvoiceURLs = async (queryPayload) => {
  try {
    const response = await retailInstance({ data: queryPayload });
    return response;
  } catch (error) {
    console.error(error, "getEInvoiceURLs error");
  }
};

const verifyCouponsService = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in verify coupons service");
  }
};

const getCustomerWallet = async (cwrCustomerId) => {
  try {
    const queryPayload = {
      query: `query {
        getCustomerWallet(customerId: "${cwrCustomerId}")
      }`,
    };

    const response = await retailInstance({ data: queryPayload });
    const rawData = response.data.data.getCustomerWallet;
    const walletData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;

    return walletData;
  } catch (error) {
    console.error("Error fetching customer wallet:", error);
    return [];
  }
};

const upsertWalletTransactionService = async (payload) => {
  try {
    const { bunitId, customerId, trxType, trxValue, tillId, payments, transactionDetails } = payload;
    const cleanedPayments = (payments ?? []).map(({ count, ...rest }) => rest);

    const query = `
  mutation {
    upsertWalletTransaction(
      wallet: {
        bunitId: "${bunitId}"
        b2c_Customer_Id: "${customerId || ""}"
        trx_type: "${trxType || ""}"
        trx_value: ${trxValue || 0}
        tillId: "${tillId || ""}"
        payments: ${JSON.stringify(cleanedPayments ?? []).replace(/"([^"]+)":/g, "$1:")}
        transactionDetails: "${JSON.stringify(transactionDetails ?? {})
          .replace(/\\/g, "\\\\")
          .replace(/"/g, '\\"')}"
      }
    ) {
      status
      message
      recordId
    }
  }
`;

    const response = await retailInstance({
      data: { query },
    });

    return response?.data?.data?.upsertWalletTransaction;
  } catch (err) {
    console.error("Error in upsertWalletTransaction:", err);
    throw err;
  }
};

const externalCoupon = async (url, token, body) => {
  try {
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("API Error:", error.response?.data || error.message);
    return {
      error: true,
      data: {
        message: error.response?.data?.data?.message || error.response?.data?.message || "Something went wrong while redeeming the coupon.",
      },
    };
  }
};

const getLoyaltyInfo = async ({ endpoint, startRow = 0, endRow = 10, loyaltyLevelId } = {}) => {
  try {
    const authHeaders = getOAuthHeaders() || {};
    const { access_token } = authHeaders;

    const response = await axios.get(endpoint, {
      headers: {
        "Content-Type": "application/json",
        ...(access_token && { Authorization: access_token }),
      },
      params: {
        startRow,
        endRow,
      },
    });

    localStorage.setItem("loyaltyData", JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    console.error("Error fetching loyalty data:", error);
    throw error;
  }
};

const getCustomerCoupons = async (customerId) => {
  try {
    const queryPayload = {
      query: `
        query GetCustomerCoupons($customerId: String!) {
          getCustomerCoupons(customerId: $customerId)
        }
      `,
      variables: {
        customerId,
      },
    };

    const response = await retailInstance({
      data: queryPayload,
    });

    return response;
  } catch (error) {
    console.error("Error in getCustomerCoupons", error);
  }
};

export {
  upsertLoyaltyTransaction,
  verifyCredit,
  upsertCreditTransaction,
  verifyLoyaltyRedemption,
  validOtp,
  verifyWalletRedemption,
  validOtpCoupon,
  sendOtpToUser,
  discardGiftCard,
  discardCouponService,
  verifyGiftCardService,
  checkGiftCard,
  upsertGiftCardTransaction,
  generateGiftVoucher,
  getEInvoiceDetails,
  getEInvoiceURLs,
  verifyCouponsService,
  getCustomerWallet,
  upsertWalletTransactionService,
  externalCoupon,
  getLoyaltyInfo,
  getCustomerCoupons,
};
